/*
  quick-nav.js

  Behaviour for sections/quick-nav.liquid: a strip of pill links that smooth scroll to sections
  further down the page and highlight themselves as those sections pass under the strip.

  From 1024px up the pills sit in a horizontal scroller and the active one slides into view.
  Below 1024px the strip collapses to a bar showing the active item and a 'View all' toggle, which
  opens the same list as an overlay panel over a scrim, with page scroll locked.

  Markup contract
  ---------------
  <quick-nav class="quick-nav__inner">              the custom element wraps everything
    <div class="quick-nav__bar">                    only shown below 1024px
      <span data-quick-nav-current>...</span>       mirrors the active link's icon and label
      <button data-quick-nav-toggle aria-expanded aria-controls="QuickNavPanel--..."
        aria-label="View all 8 sections">           name while closed, dropped while open
        <span data-quick-nav-toggle-open>View all</span>
        <span data-quick-nav-toggle-close hidden>Close</span>
    <div class="quick-nav__panel" id="QuickNavPanel--...">
      <div class="quick-nav__scroller">             the horizontal scroller from 1024px up
        <ul class="quick-nav__list">
          <li class="quick-nav__item">
            <a class="quick-nav__link" href="#calm" data-quick-nav-scroll>...</a>
    <div data-quick-nav-scrim></div>

  <a href="#calm" data-scroll-to-section>...</a>    any in-page link elsewhere on the page

  - Only links carrying data-quick-nav-scroll are managed. Links without it (page links set in
    the theme editor) navigate normally and never pick up the active state, though tapping one
    still closes the panel.
  - The active class is is-selected, matching Broadcast's own convention.
  - A data-quick-nav-scroll link whose target does not exist in the document is dropped on
    connect and left inert. A marketer typing an anchor that does not match any section must not
    take the rest of the strip down with it, which is exactly what the vendor ScrollToElement
    module in theme.js does.
  - The open state is is-open on the custom element, plus quick-nav-open on the Shopify section
    wrapper, which lifts it above the stuck header so the scrim covers the whole viewport.
  - A data-scroll-to-section link outside the element (the feature card grid cards) is handed
    to the quick nav when its hash matches one of the targets: a tab switch in tab mode, else
    the same offset scroll as a pill, with the header paused because those links usually sit
    below their target. Any other hash is left to the browser's own jump.
  - The toggle's aria-label adds the section count for screen readers. It is removed while
    the panel is open, so the name falls back to the visible 'Close' and still matches it.

  Deliberately not using the vendor module
  ----------------------------------------
  theme.js ScrollToElement.scrollSpy() dereferences both querySelector('.is-selected') and the
  resolved target without a null check, on every scroll event, and measures with offsetTop which
  is wrong inside any positioned or transformed ancestor. This reads getBoundingClientRect
  instead and guards both cases.

  Scroll offset is the height of the strip itself (the 60px bar below 1024px, since the panel is
  absolute and the scrim fixed). The section is sticky at top: 0 and ignores the header, which
  covers the strip when it slides in on scroll up, so the strip is the only thing that reliably
  overlaps content.

  Scroll lock
  -----------
  Reuses Broadcast's theme:scroll:lock / theme:scroll:unlock events, as the cart drawer does.
  Broadcast's unlock() ignores a second call made within 20ms of the first, so close() must
  dispatch unlock exactly once and do nothing when the panel is already closed.

  Tab mode
  --------
  <quick-nav data-tabs> (the 'Show linked sections as tabs on mobile' setting) turns the anchor
  targets into tab panels below 1024px: a tap shows that section and hides the other targets,
  instead of scrolling. From 1024px up nothing changes.

  - Only the targets are hidden. Sections between them are left alone, so linked sections
    should sit next to each other on the page.
  - The section starts with the URL hash target when it matches one, else the first target.
    Until this script adds quick-nav-tabs-ready to <html>, a style block in the section hides
    every target but the first, so nothing flashes before the deferred script runs.
  - Classes on the targets: quick-nav-tab-panel (every target), quick-nav-tab-hidden (inactive),
    quick-nav-tab-enter (the fade, removed on animationend), and quick-nav-tab-static (revealed
    by a switch, so its AOS items show finished rather than replaying).
  - A section revealed after load has stale AOS positions and Flickity sliders measured at zero
    width. Forcing aos-animate and dispatching one window resize fixes both: Flickity resizes
    and repositions its arrows, and AOS recalculates.
  - Every tap scrolls so the strip is stuck at the top of the window with the new tab's top
    directly under it. An upward scroll would slide the header in over the strip, so the
    header reveal is paused through window.stickyHeaderReveal until the scroll ends, the
    visitor touches, wheels or presses a key, or 1s passes (older iOS has no scrollend).
  - Scroll spy is off in tab mode. The selected tab is the active item.

  'Up next' footers
  -----------------
  <quick-nav data-tabs> with 'Show Up next' on also renders one footer per tab, in nav order:

    <div class="quick-nav__footers" hidden data-quick-nav-footers>
      <div class="quick-nav-next" data-quick-nav-next="protection">   the tab it closes
        ...status and progress...
        <a href="#confidence" data-scroll-to-section>                 the next tab, absent on the last
        <button data-quick-nav-open aria-controls="QuickNavPanel--...">

  - Liquid renders them because only the section knows the order, labels and count. Each one is
    moved to the end of its tab's section, so it shows and hides with that tab. CSS keeps them
    hidden from 1024px up and until tab mode is ready.
  - The card needs no handler of its own: once moved it is a data-scroll-to-section link outside
    the element, so onExternalClick switches tab, scrolls and focuses as for any other.
  - A footer for a missing target stays in the hidden holder; a card pointing at one is removed.
  - data-quick-nav-open opens the panel and focuses the selected item in it.
  - The moved nodes are owned by this element: they are removed on disconnect, so a theme editor
    re-render cannot duplicate them, and put back when the editor re-renders a target.
*/

if (!customElements.get('quick-nav')) {
  const selectors = {
    link: '[data-quick-nav-scroll]',
    anyLink: 'a.quick-nav__link',
    externalLink: 'a[data-scroll-to-section]',
    section: '.quick-nav',
    wrapper: '.section-quick-nav',
    bar: '.quick-nav__bar',
    panel: '.quick-nav__panel',
    scroller: '.quick-nav__scroller',
    list: '.quick-nav__list',
    current: '[data-quick-nav-current]',
    toggle: '[data-quick-nav-toggle]',
    toggleOpen: '[data-quick-nav-toggle-open]',
    toggleClose: '[data-quick-nav-toggle-close]',
    scrim: '[data-quick-nav-scrim]',
    footers: '[data-quick-nav-footers]',
    footer: '[data-quick-nav-next]',
    footerCard: 'a[data-scroll-to-section]',
    footerOpen: '[data-quick-nav-open]',
    icon: '.quick-nav__icon',
    label: '.quick-nav__label',
  };

  const classes = {
    selected: 'is-selected',
    open: 'is-open',
    wrapperOpen: 'quick-nav-open',
    tabsReady: 'quick-nav-tabs-ready',
    tabPanel: 'quick-nav-tab-panel',
    tabHidden: 'quick-nav-tab-hidden',
    tabEnter: 'quick-nav-tab-enter',
    tabStatic: 'quick-nav-tab-static',
    aosAnimate: 'aos-animate',
    hasNext: 'quick-nav-has-next',
  };

  const tabSelectors = {
    aos: '[data-aos]',
  };

  /* Fallback for browsers without scrollend. Comfortably longer than a smooth scroll. */
  const HEADER_PAUSE_MAX = 1000;

  /* Must match the max-width breakpoint in the section stylesheet. */
  const COLLAPSED_QUERY = '(max-width: 1023px)';

  class QuickNav extends HTMLElement {
    constructor() {
      super();

      this.targets = [];
      this.footers = [];
      this.current = null;
      this.ticking = false;
      this.isOpen = false;
      this.mq = window.matchMedia(COLLAPSED_QUERY);

      this.onListClick = this.onListClick.bind(this);
      this.onExternalClick = this.onExternalClick.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onToggle = this.onToggle.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onBreakpoint = this.onBreakpoint.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onBlockDeselect = this.onBlockDeselect.bind(this);
      this.onSectionSelect = this.onSectionSelect.bind(this);
      this.onSectionLoad = this.onSectionLoad.bind(this);
      this.onOpenClick = this.onOpenClick.bind(this);
      this.close = this.close.bind(this);
    }

    connectedCallback() {
      this.wrapper = this.closest(selectors.wrapper);
      this.bar = this.querySelector(selectors.bar);
      this.panel = this.querySelector(selectors.panel);
      this.scroller = this.querySelector(selectors.scroller);
      this.list = this.querySelector(selectors.list);
      this.currentLabel = this.querySelector(selectors.current);
      this.toggle = this.querySelector(selectors.toggle);
      this.toggleLabel = this.toggle ? this.toggle.getAttribute('aria-label') : null;
      this.toggleOpen = this.querySelector(selectors.toggleOpen);
      this.toggleClose = this.querySelector(selectors.toggleClose);
      this.scrim = this.querySelector(selectors.scrim);

      this.targets = Array.from(this.querySelectorAll(selectors.link))
        .map((link) => {
          const hash = link.getAttribute('href') || '';
          const id = hash.indexOf('#') === 0 ? hash.slice(1) : '';
          const target = id ? document.getElementById(id) : null;

          return target ? { link, id, target } : null;
        })
        .filter(Boolean);

      this.tabs = this.hasAttribute('data-tabs');

      if (this.list) this.list.addEventListener('click', this.onListClick);
      if (this.toggle) this.toggle.addEventListener('click', this.onToggle);
      if (this.scrim) this.scrim.addEventListener('click', this.close);
      this.mq.addEventListener('change', this.onBreakpoint);

      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
        document.addEventListener('shopify:section:select', this.onSectionSelect);
        document.addEventListener('shopify:section:load', this.onSectionLoad);
      }

      if (!this.targets.length) return;

      window.addEventListener('scroll', this.onScroll, { passive: true });
      document.addEventListener('click', this.onExternalClick);

      if (this.tabs) this.placeFooters();

      if (this.tabMode) {
        this.startTabs();
      } else {
        this.update();
      }

      /* Only now, so the pre-script style hands over to the classes without a gap. */
      if (this.tabs) document.documentElement.classList.add(classes.tabsReady);
    }

    disconnectedCallback() {
      /* A section reload in the theme editor must never leave the page locked. */
      this.close();

      if (this.list) this.list.removeEventListener('click', this.onListClick);
      if (this.toggle) this.toggle.removeEventListener('click', this.onToggle);
      if (this.scrim) this.scrim.removeEventListener('click', this.close);
      this.mq.removeEventListener('change', this.onBreakpoint);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      document.removeEventListener('shopify:section:select', this.onSectionSelect);
      document.removeEventListener('shopify:section:load', this.onSectionLoad);
      window.removeEventListener('scroll', this.onScroll);
      document.removeEventListener('click', this.onExternalClick);
      if (this.resumeHeader) this.resumeHeader();
      this.removeFooters();

      /* A reload with the setting switched off must not leave sections hidden. */
      if (this.tabs) {
        this.clearTabs(true);
        document.documentElement.classList.remove(classes.tabsReady);
      }
    }

    get tabMode() {
      return this.tabs && this.mq.matches;
    }

    /* 'auto' rather than 'smooth' when the visitor has asked for less motion. */
    get behavior() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    }

    /* Height of the sticky strip, which covers the top of whatever scrolls under it. */
    get offset() {
      const section = this.closest(selectors.section);

      return section ? section.offsetHeight : this.offsetHeight;
    }

    /*
      Any link closes the panel. Managed links then smooth scroll, a frame later when the panel
      was open, because a locked body can swallow programmatic scroll on iOS. Page links and
      dropped anchors fall through to normal navigation.
    */
    onListClick(event) {
      const link = event.target.closest(selectors.anyLink);

      if (!link) return;

      const wasOpen = this.isOpen;
      const entry = this.targets.find((item) => item.link === link);

      this.close();

      if (!entry) return;

      event.preventDefault();

      const go = this.tabMode ? () => this.showTab(entry, { scroll: true, focus: true }) : () => this.scrollToEntry(entry);

      if (wasOpen) {
        window.requestAnimationFrame(go);
      } else {
        go();
      }

      if (window.history && window.history.replaceState) {
        window.history.replaceState(null, '', link.getAttribute('href'));
      }
    }

    /*
      A data-scroll-to-section link elsewhere on the page whose hash is one of our targets. A
      modified click, a link nobody owns, or one another quick nav already took is left alone.
    */
    onExternalClick(event) {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const link = event.target.closest(selectors.externalLink);

      if (!link || this.contains(link)) return;

      const hash = link.getAttribute('href') || '';
      const id = hash.indexOf('#') === 0 ? hash.slice(1) : '';
      const entry = id ? this.targets.find((item) => item.id === id) : null;

      if (!entry) return;

      event.preventDefault();

      if (this.tabMode) {
        this.showTab(entry, { scroll: true, focus: true });
      } else {
        this.resolveTargets();
        this.pauseHeader();
        this.scrollToEntry(entry);
        this.focusTarget(entry.target);
      }

      if (window.history && window.history.replaceState) {
        window.history.replaceState(null, '', hash);
      }
    }

    /* Move focus without a second scroll, as a native hash jump would. */
    focusTarget(target) {
      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    }

    scrollToEntry(entry) {
      const top = entry.target.getBoundingClientRect().top + window.scrollY - this.offset;

      window.scrollTo({ top, left: 0, behavior: this.behavior });

      /* Highlight straight away rather than waiting for the scroll to arrive. */
      this.select(entry);
    }

    onScroll() {
      if (this.ticking) return;

      this.ticking = true;

      window.requestAnimationFrame(() => {
        this.ticking = false;
        this.update();
      });
    }

    /* The active entry is the last one whose top edge has passed under the strip. */
    update() {
      if (this.tabMode) return;

      const offset = this.offset;
      let active = null;

      this.targets.forEach((entry) => {
        if (entry.target.getBoundingClientRect().top - offset <= 1) {
          active = entry;
        }
      });

      if (active) this.select(active);
    }

    select(entry) {
      if (this.current === entry) return;

      this.targets.forEach((item) => item.link.classList.remove(classes.selected));
      entry.link.classList.add(classes.selected);
      this.current = entry;

      this.mirror(entry.link);
      this.centre(entry.link);
    }

    /*
      The browser cannot jump to a deep linked target while the pre-script style hides it, so
      jump here instead, as the native hash jump would. The vendor sliders may also have been
      built while it was hidden, so one resize lets them measure again.
    */
    startTabs() {
      const entry = this.initialEntry();
      const deepLinked = entry.id === window.location.hash.slice(1);

      this.showTab(entry, { animate: false });

      window.dispatchEvent(new Event('resize'));

      if (deepLinked) {
        const top = entry.target.getBoundingClientRect().top + window.scrollY - this.offset;

        window.scrollTo({ top, left: 0, behavior: 'auto' });
      }
    }

    /* The hash target when it is one of ours, so a shared /pages/x#calm link opens Calm. */
    initialEntry() {
      const id = window.location.hash.slice(1);

      return (id && this.targets.find((entry) => entry.id === id)) || this.targets[0];
    }

    /*
      A target section re-rendered by the theme editor is a new element, so look each one up
      again by id. Entries whose section has gone keep their last known element.
    */
    resolveTargets() {
      this.targets.forEach((entry) => {
        const target = document.getElementById(entry.id);

        if (target) entry.target = target;
      });
    }

    /*
      Show one target and hide the rest. Targets are compared by element, so two links pointing
      at the same section cannot hide it from each other.
    */
    showTab(entry, { animate = true, scroll = false, focus = false } = {}) {
      if (!entry) return;

      this.resolveTargets();

      const target = entry.target;
      const wasHidden = target.classList.contains(classes.tabHidden);

      this.targets.forEach((item) => {
        item.target.classList.add(classes.tabPanel);
        if (item.target !== target) item.target.classList.add(classes.tabHidden);
        item.link.removeAttribute('aria-current');
      });

      target.classList.remove(classes.tabHidden);
      entry.link.setAttribute('aria-current', 'true');

      if (wasHidden) this.reveal(target, animate);

      if (scroll) this.scrollToTab(target);

      if (focus) this.focusTarget(target);

      this.select(entry);
    }

    reveal(target, animate) {
      target.classList.add(classes.tabStatic);
      target.querySelectorAll(tabSelectors.aos).forEach((item) => item.classList.add(classes.aosAnimate));

      if (animate && this.behavior === 'smooth') {
        target.classList.remove(classes.tabEnter);
        void target.offsetWidth;
        target.classList.add(classes.tabEnter);
        target.addEventListener('animationend', () => target.classList.remove(classes.tabEnter), { once: true });
      }

      window.dispatchEvent(new Event('resize'));
    }

    /* Pin the strip to the top of the window, with the tab's top directly under it. */
    scrollToTab(target) {
      const top = target.getBoundingClientRect().top + window.scrollY - this.offset;

      if (Math.abs(top - window.scrollY) <= 1) return;

      this.pauseHeader();
      window.scrollTo({ top, left: 0, behavior: this.behavior });
    }

    /* Keep the sticky header from sliding in over the strip while the page scrolls to a tab. */
    pauseHeader() {
      const header = window.stickyHeaderReveal;

      if (!header || !header.pause) return;

      if (this.resumeHeader) this.resumeHeader();

      const events = ['scrollend', 'touchstart', 'wheel', 'keydown'];
      let timer = null;

      const stop = () => {
        window.clearTimeout(timer);
        events.forEach((name) => window.removeEventListener(name, resume));
        this.resumeHeader = null;
      };

      /*
        Broadcast dispatches theme:scroll a frame after the native scroll, so wait two frames
        for the last upward event to land while still paused. A teardown resumes straight away.
      */
      const resume = () => {
        stop();
        window.requestAnimationFrame(() => window.requestAnimationFrame(() => header.resume()));
      };

      header.pause();
      events.forEach((name) => window.addEventListener(name, resume, { passive: true }));
      timer = window.setTimeout(resume, HEADER_PAUSE_MAX);
      this.resumeHeader = () => {
        stop();
        header.resume();
      };
    }

    /* Leaving tab mode shows every target again. Static stays unless the element is going away. */
    clearTabs(all = false) {
      this.resolveTargets();

      this.targets.forEach((item) => {
        item.target.classList.remove(classes.tabHidden, classes.tabEnter, classes.tabPanel);
        if (all) item.target.classList.remove(classes.tabStatic);
        item.link.removeAttribute('aria-current');
      });
    }

    /* Move each 'Up next' footer to the end of the tab it closes. */
    placeFooters() {
      const holder = this.querySelector(selectors.footers);

      if (!holder) return;

      Array.from(holder.querySelectorAll(selectors.footer)).forEach((footer) => {
        const entry = this.targets.find((item) => item.id === footer.dataset.quickNavNext);

        if (!entry) return;

        const card = footer.querySelector(selectors.footerCard);
        const nextId = card ? (card.getAttribute('href') || '').slice(1) : '';

        if (card && !this.targets.some((item) => item.id === nextId)) card.remove();

        footer.querySelectorAll(selectors.footerOpen).forEach((button) => {
          button.addEventListener('click', this.onOpenClick);
        });

        this.footers.push({ entry, footer });
        this.attachFooter(entry, footer);
      });
    }

    attachFooter(entry, footer) {
      entry.target.appendChild(footer);
      entry.target.classList.add(classes.hasNext);
    }

    removeFooters() {
      this.footers.forEach(({ entry, footer }) => {
        footer.querySelectorAll(selectors.footerOpen).forEach((button) => {
          button.removeEventListener('click', this.onOpenClick);
        });

        footer.remove();
        entry.target.classList.remove(classes.hasNext);
      });

      this.footers = [];
    }

    /*
      'See all' at the bottom of a tab. The strip is stuck at the top by then, so the panel opens
      in view; focus moves into it, since the button is about to sit under the scrim.
    */
    onOpenClick() {
      this.open();

      if (!this.isOpen) return;

      const link = this.current ? this.current.link : this.list && this.list.querySelector(selectors.anyLink);

      if (link) window.requestAnimationFrame(() => link.focus({ preventScroll: true }));
    }

    /* Copy the active link's icon and label into the collapsed bar. */
    mirror(link) {
      if (!this.currentLabel) return;

      const parts = [link.querySelector(selectors.icon), link.querySelector(selectors.label)]
        .filter(Boolean)
        .map((node) => node.cloneNode(true));

      this.currentLabel.replaceChildren(...parts);
    }

    /*
      Centre the pill inside the strip without touching page scroll. scrollIntoView is not usable
      here because it scrolls every scrollable ancestor, the document included, which would fight
      the page scroll that triggered this in the first place. Below 1024px the list wraps, so the
      scroller never overflows and this returns early.
    */
    centre(link) {
      const scroller = this.scroller;

      if (!scroller || scroller.scrollWidth <= scroller.clientWidth) return;

      const left =
        link.getBoundingClientRect().left -
        scroller.getBoundingClientRect().left +
        scroller.scrollLeft -
        (scroller.clientWidth - link.clientWidth) / 2;

      scroller.scrollTo({ left, behavior: this.behavior });
    }

    onToggle() {
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }
    }

    open() {
      if (this.isOpen || !this.toggle || !this.mq.matches) return;

      this.isOpen = true;

      /*
        Fit the panel between the bar and the bottom of the viewport. The bar is not always at
        the top: the panel can be opened before the strip has stuck.
      */
      const barBottom = this.bar ? this.bar.getBoundingClientRect().bottom : 0;
      this.style.setProperty('--quick-nav-panel-max', `${Math.max(window.innerHeight - barBottom, 0)}px`);

      this.classList.add(classes.open);
      if (this.wrapper) this.wrapper.classList.add(classes.wrapperOpen);
      this.setToggleState(true);

      document.addEventListener('keydown', this.onKeydown);
      document.dispatchEvent(new CustomEvent('theme:scroll:lock', { bubbles: true, detail: this.panel }));
    }

    close() {
      if (!this.isOpen) return;

      this.isOpen = false;

      this.classList.remove(classes.open);
      if (this.wrapper) this.wrapper.classList.remove(classes.wrapperOpen);
      this.setToggleState(false);

      /* Focus inside the panel is about to become hidden, so hand it back to the toggle. */
      if (this.panel && this.panel.contains(document.activeElement)) {
        this.toggle.focus({ preventScroll: true });
      }

      document.removeEventListener('keydown', this.onKeydown);
      document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
    }

    setToggleState(open) {
      this.toggle.setAttribute('aria-expanded', String(open));

      if (open) {
        this.toggle.removeAttribute('aria-label');
      } else if (this.toggleLabel) {
        this.toggle.setAttribute('aria-label', this.toggleLabel);
      }

      if (this.toggleOpen) this.toggleOpen.hidden = open;
      if (this.toggleClose) this.toggleClose.hidden = !open;
    }

    onKeydown(event) {
      if (event.key !== 'Escape') return;

      this.close();
      this.toggle.focus({ preventScroll: true });
    }

    onBreakpoint() {
      if (!this.mq.matches) this.close();

      if (!this.tabs || !this.targets.length) return;

      if (this.mq.matches) {
        this.showTab(this.current || this.initialEntry(), { animate: false });
      } else {
        this.clearTabs();
        this.update();
      }
    }

    /* Theme editor only: selecting a hidden target section in the sidebar shows its tab. */
    onSectionSelect(event) {
      if (!this.tabMode) return;

      const entry = this.targets.find((item) => event.target.contains(document.getElementById(item.id)));

      if (entry) this.showTab(entry, { animate: false, scroll: true });
    }

    /* Theme editor only: a re-rendered target arrives without its tab classes or its footer. */
    onSectionLoad(event) {
      const reloaded = this.footers.filter(({ entry }) => event.target.contains(document.getElementById(entry.id)));

      if (reloaded.length) {
        this.resolveTargets();
        reloaded.forEach(({ entry, footer }) => this.attachFooter(entry, footer));
      }

      if (!this.tabMode || !this.current) return;

      if (this.targets.some((item) => event.target.contains(document.getElementById(item.id)))) {
        this.showTab(this.current, { animate: false });
      }
    }

    /* Theme editor only: show the panel when one of its blocks is selected on a small screen. */
    onBlockSelect(event) {
      if (this.contains(event.target)) this.open();
    }

    onBlockDeselect(event) {
      if (this.contains(event.target)) this.close();
    }
  }

  customElements.define('quick-nav', QuickNav);
}
